import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { FaGithub } from 'react-icons/fa';

const AboutPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Bar */}
      <nav className="bg-red-800 text-white py-4">
        <div className=" px-4 flex justify-between items-center">
          <Link to="/" className="text-xl">Experimental-Catalysis-Hub.org</Link>
          <div className="flex space-x-6">
            <Link to="/publications">Publications</Link>
            <Link to="/search"> Catalyst Materials</Link>
            <Link to="/about">About</Link>
            <Link to="/about">Documentation</Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* About Section */}
        <div className="bg-white p-8 mb-6 shadow rounded-sm border border-gray-300">
          <h2 className="text-2xl font-bold mb-4">About Experimental-Catalysis-Hub.org</h2>
          <p className="mb-4">
            Experimental-Electrocatalysis-Hub.org is a web-platform for sharing experimental electrocatalysis data. The database collects and organizes catalyst synthesis, characterization, and electrochemical testing data to support reproducible and data-driven catalyst discovery.
          </p>
          <p className="mb-4">
            Under Datasets, full experimental workflows can be browsed for each study. These include catalyst compositions, synthesis parameters, XRD/XPS/XAS characterization, and raw electrochemical measurements such as CV curves and activity metrics. With a growing number of contributions, the platform offers user-friendly access to complete experimental records instead of only summary values, helping researchers compare results across materials, morphologies, and reaction conditions.
          </p>
          <p className="mb-4">
            Ultimately, the goal is to create a consistent and FAIR (Findable, Accessible, Interoperable, Reusable) database for experimental electrocatalysis data. By providing well-structured datasets, the platform enables clearer comparisons, supports machine-learning model development, and encourages transparent reporting within the community. The resource also serves as a natural starting point for data-driven studies, benchmarking, and integration with computational workflows.
          </p>
          <p className="mb-4">
            Features include search tools for filtering by reaction type, catalyst composition, morphology, synthesis route, and electrochemical metrics. Built-in visualization plots allow interactive exploration of XRD, XPS spectra and CV curves directly in the browser. All datasets are linked to publications, and data can be accessed programmatically through a public GraphQL and Python API for automated analysis pipelines.
          </p>
          <p className="mb-4">
            All software components for this project are open-source to support community engagement and collaboration. The frontend is built with modern web frameworks for responsive and intuitive data exploration, while the backend provides structured APIs for both human-readable and machine-readable access. New visualization modules and analysis apps can be added easily as the platform grows.
          </p>
          <p>
             As such, Experimental-Electrocatalysis-Hub.org aims to serve as a shared, community-driven resource for trend analysis, reproducible experimental comparisons, and machine-learning-ready electrocatalysis datasets
          </p>
        </div>

        {/* How to Cite Section */}
        <div className="bg-white p-8 mb-6 shadow rounded-sm border border-gray-300">
          <h2 className="text-2xl font-bold mb-4">How to cite</h2>
          <p className="mb-4">When referring to the Experimental-Catalysis-Hub platform in general, please cite the following publication:</p>
          <div className="pl-8 mb-6 bg-gray-50 p-4 rounded border-l-4 border-red-800">
            <p className="mb-2">Ruchika Mahajan; Ashton M. Aleman; Colin F. Crago; Suman Bhasker-Ranganath; Melissa E. Kreider; Jose A. Zamora Zeledon; Johanna Schröder; Gaurav A. Kamat; McKenzie A. Hubert; Adam C. Nielander; Thomas F. Jaramillo; Michaela Burke Stevens; Johannes Voss; Kirsten T. Winther</p>
            <p className="font-semibold mb-1">A research database for experimental electrocatalysis: Advancing data sharing and reusability</p>
            <p className="italic mb-2">J. Chem. Phys. 163, 124704 (2025)</p>
            <a href="https://doi.org/10.1063/5.0280821" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">https://doi.org/10.1063/5.0280821</a>
          </div>
          
          <div className="mt-6 bg-red-50 p-4 rounded border border-red-800">
            <p className="font-semibold mb-2">Citation Guidelines:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Each dataset on this platform is linked to a specific publication</li>
              <li>If you use data from this website, please also cite the related publication.</li>
              <li>You can find the DOI link with each dataset to access and cite the original research</li>
            </ul>
          </div>
        </div>


        {/* License Section
        <div className="bg-white p-8 mb-6 shadow rounded-sm border border-gray-300">
          <h2 className="text-2xl font-bold mb-4">License</h2>
          <p className="mb-4">
            Except where otherwise noted, content on Experimental-Catalysis-Hub is licensed under a{' '}
            <a href="#" className="text-blue-600 hover:underline">
              Creative Commons Attribution 4.0 International License
            </a>.
          </p>
          <img src="/img.png" alt="Creative Commons License" className="h-8" />
        </div> */}



        {/* Combined People and Contact & Contributors Section */}
        <div className="bg-white p-8 mb-6 shadow rounded-sm border border-gray-300">
          {/* People and Contact */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">People and Contact</h2>
            <p className="mb-6">
              Experimental-Catalysis-Hub is developed by the SUNCAT Center for Interface Science and Catalysis, SLAC National Accelerator Laboratory, Stanford
              University. Contact information for the primary people involved is provided below. For technical support please contact Kirsten Winther at{' '}
              <a href="mailto:winther@stanford.edu" className="text-blue-600 hover:underline">
                winther@stanford.edu
              </a>.
            </p>

            {/* Profile Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              {/* Profile 1 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/kirsten_winther.png"
                  alt="Kirsten Winther"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/kirsten-winther" className="text-blue-600 hover:underline">Kirsten Winther</a>
                </p>
                <p className="text-sm text-gray-600">Associate Staff Scientist, SUNCAT, SLAC National Accelerator Laboratory | Developer of the Experimental Catalysis Hub</p>
              </div>

              {/* Profile 2 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/johannes.jpg"
                  alt="Johannes Voss"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/johannes-voss" className="text-blue-600 hover:underline">Johannes Voss</a>
                </p>
                <p className="text-sm text-gray-600">Staff Scientist, SUNCAT, SLAC National Accelerator Laboratory</p>
              </div>
              {/* Profile 3 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/Jaramillo.jpg"
                  alt="Thomas F. Jaramillo"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/thomas-francisco-jaramillo" className="text-blue-600 hover:underline"> Thomas F. Jaramillo</a>
                </p>
                <p className="text-sm text-gray-600">Associate Professor, Department of Chemical Engineering, SUNCAT, SLAC National Accelerator Laboratory, Stanford University</p>
              </div>

              {/* Profile 4 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/Adam-Nielander.jpg"
                  alt="Johannes Voss"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/adam-nielander" className="text-blue-600 hover:underline">Adam Nielander </a>
                </p>
                <p className="text-sm text-gray-600">Associate Scientist , SUNCAT, SLAC National Accelerator Laboratory</p>
              </div>

              {/* Profile 5 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/bajdich.jpg"
                  alt="Michal Bajdich"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/michal-bajdich" className="text-blue-600 hover:underline">Michal Bajdich</a>
                </p>
                <p className="text-sm text-gray-600">Staff Scientist, SUNCAT, SLAC National Accelerator Laboratory</p>
              </div>
              {/* Profile 6 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/abild_frank.jpg"
                  alt="Frank Abild-Pedersen"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/frank-abild-pedersen" className="text-blue-600 hover:underline">Frank Abild-Pedersen</a>
                </p>
                <p className="text-sm text-gray-600">Senior Staff Scientist, SUNCAT, SLAC National Accelerator Laboratory</p>
              </div>
            </div>
          </div>

          {/* Contributors */}
          <div>
            <h2 className="text-2xl font-bold mb-4">Contributors</h2>
            <p className="mb-6">SUNCAT-affiliated <strong>developers</strong>  and <strong> data curators</strong> of Experimental-Catalysis-Hub:</p>


            {/* Contributors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Contributor 1 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/ruchika_mahajan.png"
                  alt="Ruchika Mahajan"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/ruchika-mahajan" className="text-blue-600 hover:underline">Ruchika Mahajan</a>
                </p>
                <p className="text-sm text-gray-600"> Postdoctoral Scholar, SUNCAT, SLAC National Accelerator Laboratory, Stanford University </p>
              </div>
{/* 
              Add more contributors similarly
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/kirsten_winther.png"
                  alt="Kirsten Winther"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="http://suncat.stanford.edu/people/kirsten-winther" className="text-blue-600 hover:underline">Kirsten Winther</a>
                </p>
                <p className="text-sm text-gray-600">Associate Staff Scientist, SUNCAT, SLAC National Accelerator Laboratory</p>
              </div> */}

              {/* Add more contributors similarly 3 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/suman.png"
                  alt="Suman Bhasker Ranganath"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/suman-bhasker-ranganath" className="text-blue-600 hover:underline">Suman Bhasker Ranganath
                  </a>
                </p>
                <p className="text-sm text-gray-600">Postdoctoral Scholar, SUNCAT, SLAC National Accelerator Laboratory, Stanford University</p>
              </div>

              {/* Add more contributors similarly 4 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/ashton_aleman.png"
                  alt="Ashton Mackenzie Aleman"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/ashton-mackenzie-aleman" className="text-blue-600 hover:underline">Ashton Mackenzie Aleman</a>
                </p>
                <p className="text-sm text-gray-600">Graduate Student, SUNCAT, SLAC National Accelerator Laboratory, Stanford University</p>
              </div>

              {/* Add more contributors similarly 5 */}
              <div className="flex flex-col items-center text-center">
                <img
                  src="/images/Colin.jpeg"
                  alt="Colin Crago"
                  className="w-32 h-32 rounded-full object-cover border-2 border-gray-200 mb-4"
                />
                <p className="font-medium mb-1">
                  <a href="https://suncat.stanford.edu/people/colin-crago" className="text-blue-600 hover:underline">Colin Crago</a>
                </p>
                <p className="text-sm text-gray-600">Graduate Student, SUNCAT, SLAC National Accelerator Laboratory, Stanford University</p>
              </div>

            </div>
          </div>

          {/* Previously SUNCAT-affiliated developers */}
          <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Previously SUNCAT-affiliated contributors</h3>
              <ul className="space-y-2 text-gray-700">
                <li>
                  <a href="https://suncat.stanford.edu/people/michaela-stevens" className="text-blue-600 hover:underline font-medium">Michaela Burke Stevens </a> 
                </li>
                <li>
                  <a href="#" className="text-blue-600 hover:underline font-medium">Melissa E. Kreider</a> 
                </li>
                <li>
                  <a href="#" className="text-blue-600 hover:underline font-medium">Jose A. Zamora Zeledon </a> 
                </li>
                <li>
                  <a href="https://suncat.stanford.edu/people/johanna-schroder" className="text-blue-600 hover:underline font-medium">Johanna Schröder </a>
                </li>
                <li>
                  <a href="https://suncat.stanford.edu/people/gaurav-kamat" className="text-blue-600 hover:underline font-medium">Gaurav A. Kamat </a> 
                </li>
                <li>
                  <a href="https://suncat.stanford.edu/people/mckenzie-austin-hubert" className="text-blue-600 hover:underline font-medium">McKenzie A. Hubert </a>
                </li>
              </ul>
            </div>


        </div>

        {/* Partners and Support Section */}
        <div className="bg-white p-8 mb-6 shadow rounded-sm border border-gray-300">
          <h2 className="text-2xl font-bold mb-8">Partners and Support</h2>
          <div className="flex items-center justify-start space-x-8 px-4">
            <img src="/images/slac_logo1.jpg" alt="SLAC" className="h-16 w-auto" />
            <img src="/images/stanford_logo.png" alt="Stanford" className="h-24 w-auto" />
            <img src="/images/DOE_logo.png" alt="U.S. DEPARTMENT OF ENERGY" className="h-20 w-auto" />
          </div>
        </div>
      </div>

\
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">


        {/* Bottom footer with Stanford branding and navigation */}
        <div className="bg-red-800 text-white py-16">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between">
                    {/* Stanford University */}
                    <div className="flex items-center">
                        <span className="text-2xl font-serif">Stanford</span>
                        <span className="text-sm ml-1">University</span>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex items-center space-x-8">
                        <Link to="/docs" className="flex items-center hover:opacity-80">
                            <span className="mr-3 text-2xl">📄</span> Docs
                        </Link>
                        <Link to="https://github.com" className="flex items-center hover:opacity-80">
                            <FaGithub className="mr-3 text-2xl" /> Visit us on GitHub
                        </Link>
                        <Link to="/license" className="flex items-center hover:opacity-80">
                            <span className="mr-3 text-2xl">©️</span> License
                        </Link>
                    </div>
                </div>
            </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;